import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Self-service timeclock punch for hourly office staff (Issue #516).
 *
 * Lives in the office top bar (`top-bar-v3`). Renders nothing for non-hourly
 * users. While clocked in it shows a live elapsed timer and sends an activity
 * heartbeat (`/time-clock/ping`) every ~2 min while the tab is visible so the
 * idle auto-clock-out sweep knows the staffer is still working.
 */
interface CurrentClock {
    clockedIn: boolean;
    entry: { id: string; clock_in: string } | null;
}

function elapsedLabel(clockIn: string, now: number): string {
    const totalMin = Math.max(0, Math.floor((now - new Date(clockIn).getTime()) / 60_000));
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const PING_INTERVAL_MS = 2 * 60 * 1000;

export function TimeClockPunch() {
    const { isHourly } = useAuth();
    const qc = useQueryClient();
    const [now, setNow] = useState(() => Date.now());

    const { data } = useQuery({
        queryKey: ["time-clock", "current"],
        queryFn: () => api.get<CurrentClock>("/api/time-clock/current"),
        refetchInterval: 60_000,
        enabled: isHourly,
    });
    const clockedIn = data?.clockedIn ?? false;

    const clockIn = useMutation({
        mutationFn: () => api.post("/api/time-clock/in"),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["time-clock"] });
            toast.success("Clocked in — have a good day.");
        },
        onError: () => toast.error("Could not clock in. Try again."),
    });
    const clockOut = useMutation({
        mutationFn: () => api.post("/api/time-clock/out"),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["time-clock"] });
            toast.success("Clocked out. See you next time.");
        },
        onError: () => toast.error("Could not clock out. Try again."),
    });

    // Re-render the elapsed timer while clocked in.
    useEffect(() => {
        if (!clockedIn) return;
        const t = setInterval(() => setNow(Date.now()), 30_000);
        return () => clearInterval(t);
    }, [clockedIn]);

    // Activity heartbeat — keep last_activity_at fresh while clocked in + visible.
    useEffect(() => {
        if (!clockedIn) return;
        const ping = () => {
            if (document.visibilityState === "visible") {
                api.post("/api/time-clock/ping").catch(() => {});
            }
        };
        ping();
        const t = setInterval(ping, PING_INTERVAL_MS);
        document.addEventListener("visibilitychange", ping);
        return () => {
            clearInterval(t);
            document.removeEventListener("visibilitychange", ping);
        };
    }, [clockedIn]);

    if (!isHourly) return null;

    const busy = clockIn.isPending || clockOut.isPending;

    if (clockedIn && data?.entry) {
        const label = elapsedLabel(data.entry.clock_in, now);
        return (
            <button
                className="door"
                disabled={busy}
                onClick={() => clockOut.mutate()}
                title="Clock out for the day"
                aria-label={`Clocked in ${label}. Click to clock out.`}
            >
                <span className="dot" style={{ background: "#16a34a" }} /> {label} · Clock out
            </button>
        );
    }

    return (
        <button
            className="door"
            disabled={busy}
            onClick={() => clockIn.mutate()}
            title="Clock in to start your day"
            aria-label="Clock in"
        >
            <span className="dot" style={{ background: "#9ca3af" }} /> Clock in
        </button>
    );
}
